import React, { useEffect, useState } from 'react';
import { Card, Table, Button, message, App } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import dayjs from 'dayjs';
import { draftService } from '../../services/draftService';

interface DraftTmp {
  Draft_Tmp_IDX: number;
  Draft_Subject: string;
  Draft_Cost: number;
  Draft_Client: string;
  Reg_Date: string;
}

const DraftTmpList: React.FC = () => {
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DraftTmp[]>([]);

  useEffect(() => {
    fetchTmpList();
  }, []);

  const fetchTmpList = async () => {
    setLoading(true);
    try {
      const response = await draftService.getTmpList();
      if (response.resultCode === 200) {
        setDataSource(response.data);
      } else {
        message.error(response.message || '임시보관함 조회 실패');
      }
    } catch (error) {
      console.error('임시보관함 조회 오류:', error);
      message.error('임시보관함을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지

    modal.confirm({
      title: '임시 기안서 삭제',
      content: '정말 이 임시 기안서를 삭제하시겠습니까?',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          const result = await draftService.deleteTmp(idx);
          if (result.resultCode === 200) {
            message.success('임시 기안서가 삭제되었습니다.');
            fetchTmpList(); // 목록 새로고침
          } else {
            message.error(result.message || '삭제 실패');
          }
        } catch (error) {
          console.error('삭제 오류:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  const columns: ColumnsType<DraftTmp> = [
    {
      title: 'No',
      dataIndex: 'Draft_Tmp_IDX',
      key: 'Draft_Tmp_IDX',
      width: 80,
      align: 'center',
    },
    {
      title: '제목',
      dataIndex: 'Draft_Subject',
      key: 'Draft_Subject',
      ellipsis: true,
    },
    {
      title: '거래처',
      dataIndex: 'Draft_Client',
      key: 'Draft_Client',
      width: 150,
    },
    {
      title: '금액',
      dataIndex: 'Draft_Cost',
      key: 'Draft_Cost',
      width: 120,
      align: 'right',
      render: (value) => value ? value.toLocaleString() + '원' : '-',
    },
    {
      title: '저장일',
      dataIndex: 'Reg_Date',
      key: 'Reg_Date',
      width: 120,
      align: 'center',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '삭제',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => handleDelete(e, record.Draft_Tmp_IDX)}
        >
          삭제
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card title="임시보관함">
        <p style={{ marginBottom: 16, color: '#666' }}>
          임시 저장된 기안서를 클릭하면 불러와서 계속 작성할 수 있습니다.
        </p>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="Draft_Tmp_IDX"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `전체 ${total}건`,
          }}
          onRow={(record) => ({
            onClick: () => {
              // 임시 기안서 불러오기 페이지로 이동
              navigate(`/draft/tmp-view/${record.Draft_Tmp_IDX}`);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </MainLayout>
  );
};

export default DraftTmpList;
